import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, filter, skip, take } from 'rxjs/operators';
import * as moment from 'moment';
import { OrderService } from 'src/app/services/pedidos/pedidos.service';
import { OfflineStorageService } from './offline-storage.service';
import { OrdersListRefreshService } from './orders-list-refresh.service';
import { Order } from 'src/app/shared/models/orders';
import { ExecutionContextService } from 'src/core/context/execution-context.service';

const WARMUP_COOLDOWN_MS   = 5 * 60 * 1000;        // 5 minutes between warmup runs
const FULL_CLEAR_INTERVAL_MS = 2 * 24 * 60 * 60 * 1000; // 2 days between full cache clears
const LAST_CLEAR_KEY = 'polpaico_last_cache_clear';

@Injectable({ providedIn: 'root' })
export class CacheWarmupService {

  /** Emits whenever the orders list cache is successfully refreshed from the network. */
  readonly ordersUpdated$ = new Subject<void>();

  private isWarming = false;
  private isRefreshingOrders = false;
  private pendingOrdersRefresh = false;
  private lastWarmupAt = 0;
  private initialized = false;
  private lastUserRut = '';

  constructor(
    private orderService: OrderService,
    private offlineStorage: OfflineStorageService,
    private readonly ordersListRefresh: OrdersListRefreshService,
    private readonly executionContext: ExecutionContextService
  ) {
    this.ordersListRefresh.refreshRequested$
      .pipe(debounceTime(400))
      .subscribe(() => {
        this.refreshOrdersListNow().catch((e) =>
          console.warn('[CacheWarmup] refreshOrdersListNow failed:', e)
        );
      });
  }

  init(): void {
    // Detect user switch — clear stale cache and bypass cooldown so the new user's
    // data is fetched immediately instead of serving the previous user's IndexedDB data.
    const currentUserRut: string =
      JSON.parse(localStorage.getItem('user') ?? 'null')?.RUT_USUARIO ?? '';
    if (currentUserRut && this.lastUserRut && currentUserRut !== this.lastUserRut) {
      this.offlineStorage.clearAllCache().catch(() => null);
      this.lastWarmupAt = 0;
      // console.log('[CacheWarmup] User changed — cache cleared for new user');
    }
    this.lastUserRut = currentUserRut;

    if (this.initialized) return;
    this.initialized = true;

    // Run immediately on app open
    this.warmUp();

    // Transitioning from offline → online is a user-relevant signal (they left a dead
    // zone, reopened the app, etc.) — always force a fresh pull, don't let the 5-minute
    // cooldown swallow it.
    this.offlineStorage.online$
      .pipe(skip(1), filter(online => online))
      .subscribe(() => this.forceWarmUp());
  }

  /**
   * Forces an immediate warmup, bypassing the cooldown guard.
   * Use for explicit user-triggered refreshes (e.g. "Actualizar" button).
   */
  async forceWarmUp(): Promise<void> {
    this.lastWarmupAt = 0;
    await this.warmUp();
  }

  /**
   * Refresca solo el listado de pedidos (POST /viajes) sin cooldown ni PDFs.
   * Usado tras set-values exitoso (SyncService / OrderService).
   */
  async refreshOrdersListNow(): Promise<void> {
    if (!this.offlineStorage.isOnline()) return;
    if (!this.orderService.hasAuthKey()) return;
    if (this.isSoloControlCombustible()) return;
    if (this.isRefreshingOrders) {
      this.pendingOrdersRefresh = true;
      return;
    }
    this.isRefreshingOrders = true;
    try {
      await this.cacheOrders();
    } finally {
      this.isRefreshingOrders = false;
      if (this.pendingOrdersRefresh) {
        this.pendingOrdersRefresh = false;
        await this.refreshOrdersListNow();
      }
    }
  }

  /**
   * Performs one warmup pass. Guarded by isWarming and lastWarmupAt cooldown
   * so concurrent or too-frequent calls are safely ignored.
   */
  /**
   * Cancela refrescos pendientes (p. ej. al cerrar sesión) para evitar 401 en consola.
   */
  cancelPending(): void {
    this.isWarming = false;
    this.isRefreshingOrders = false;
    this.pendingOrdersRefresh = false;
  }

  /** Reinicia estado de sesión tras logout para que el próximo login haga init limpio. */
  resetSession(): void {
    this.cancelPending();
    this.initialized = false;
    this.lastUserRut = '';
    this.lastWarmupAt = 0;
  }

  async warmUp(): Promise<void> {
    if (!this.offlineStorage.isOnline()) return;
    if (!this.orderService.hasAuthKey()) return;
    if (this.isSoloControlCombustible()) return;
    if (this.isWarming) return;
    if (Date.now() - this.lastWarmupAt < WARMUP_COOLDOWN_MS) return;

    this.isWarming = true;

    try {
      await this.clearCacheIfStale();
      await Promise.all([
        this.cacheOrders().catch(e => console.warn('[CacheWarmup] orders failed:', e)),
        this.cacheReferenceData().catch(e => console.warn('[CacheWarmup] reference data failed:', e)),
      ]);
      this.lastWarmupAt = Date.now();
    } finally {
      this.isWarming = false;
    }
  }

  // ─── Full cache clear ─────────────────────────────────────────────────────

  private async clearCacheIfStale(): Promise<void> {
    const raw = localStorage.getItem(LAST_CLEAR_KEY);
    const lastClear = raw ? parseInt(raw, 10) : 0;
    if (Date.now() - lastClear < FULL_CLEAR_INTERVAL_MS) return;

    await this.offlineStorage.clearAllCache();
    localStorage.setItem(LAST_CLEAR_KEY, Date.now().toString());
    // console.log('[CacheWarmup] Full cache cleared (2-day interval)');
  }

  // ─── Orders ───────────────────────────────────────────────────────────────

  private async cacheOrders(): Promise<void> {
    if (!this.orderService.hasAuthKey()) return;

    const fetched =
      (await this.orderService.fetchOrdersDirect().pipe(take(1)).toPromise()) ?? [];
    const orders = await this.orderService.mergeOrdersForCache(fetched);
    await this.offlineStorage.saveOrders(orders);

    this.ordersUpdated$.next();

    const yesterday = moment().subtract(1, 'days');
    const pending = orders.filter(o => o.flag_pending === '1');
    const active  = orders.filter(o =>
      o.status < 6 &&
      o.status !== 0 &&
      o.rejectionFlag === '' &&
      !o.isSignature &&
      !o.isNotSigned &&
      moment(o.orderDate, 'DD/MM/YYYY').isAfter(yesterday)
    );
    const relevant = [...new Map([...pending, ...active].map(o => [o.tkt, o])).values()];

    const relevantTkts = new Set(relevant.map(o => o.tkt.toString()));
    const cachedTkts = await this.offlineStorage.getCachedTkts();

    const toDelete = [...cachedTkts].filter(tkt => !relevantTkts.has(tkt));
    await Promise.all(toDelete.map(tkt => this.offlineStorage.clearTktCache(tkt).catch(() => null)));

    const cachedPdfTkts = await this.offlineStorage.getCachedPdfTkts();

    // For new tkts: fetch detail, then cache PDF using the detail URL
    const toAdd = relevant.filter(o => !cachedTkts.has(o.tkt.toString()));
    await this.processWithConcurrency(toAdd, 2, async (listOrder: Order) => {
      await this.cacheOrderDetail(listOrder.tkt.toString()).catch(() => null);
      if (!cachedPdfTkts.has(listOrder.tkt.toString())) {
        const detail = await this.offlineStorage.getOrderDetail(listOrder.tkt.toString()).catch(() => null);
        const pdfUrl = detail?.routePdfGuide || listOrder.routePdfGuide;
        if (pdfUrl) await this.cachePdf({ ...listOrder, routePdfGuide: pdfUrl }).catch(() => null);
      }
    });

    // For already-cached details missing a PDF
    const alreadyCached = relevant.filter(o =>
      cachedTkts.has(o.tkt.toString()) && !cachedPdfTkts.has(o.tkt.toString())
    );
    await this.processWithConcurrency(alreadyCached, 2, async (listOrder: Order) => {
      const detail = await this.offlineStorage.getOrderDetail(listOrder.tkt.toString()).catch(() => null);
      const pdfUrl = detail?.routePdfGuide || listOrder.routePdfGuide;
      if (pdfUrl) await this.cachePdf({ ...listOrder, routePdfGuide: pdfUrl }).catch(() => null);
    });
  }

  private async cacheOrderDetail(tkt: string): Promise<void> {
    try {
      const detail = await this.orderService.getOrder(tkt, true).pipe(take(1)).toPromise();
      if (detail?.[0]) {
        await this.offlineStorage.saveOrderDetail(tkt, detail[0]);
      }
    } catch (e) {
      console.warn(`[CacheWarmup] detail failed for tkt ${tkt}:`, e);
    }
  }

  /**
   * Fetches and stores a single PDF by tkt + URL. Called by warmup and by the PDF viewer on-demand.
   *
   * Defensive against cache poisoning: many failure modes (expired session, third-party cookie
   * blocking, server under load) return HTTP 200 with an HTML login/error page instead of the PDF.
   * We validate the Content-Type AND the %PDF- magic bytes before storing, and proactively evict
   * any previously-poisoned entry for this tkt when validation fails.
   */
  async savePdfToCache(tkt: string, url: string): Promise<boolean> {
    if (!url) return false;
    try {
      // IMPORTANT: do NOT set credentials: 'include'. The PDF endpoint responds with
      // Access-Control-Allow-Origin: * (public endpoint), which is incompatible with
      // credentialed requests and would cause every cross-origin fetch to hard-fail CORS.
      // Authentication, when needed, rides on the WebView's document-level cookie jar,
      // not this script fetch.
      const response = await fetch(url);
      if (!response.ok) {
        await this.offlineStorage.clearTktCache(tkt).catch(() => null);
        return false;
      }
      const blob = await response.blob();

      if (!(await this.isValidPdfBlob(blob, response))) {
        console.warn(`[CacheWarmup] non-PDF response for tkt ${tkt}; evicting any cached entry`);
        await this.offlineStorage.clearTktCache(tkt).catch(() => null);
        return false;
      }

      const base64 = await this.blobToBase64(blob);
      const filename = this.extractFilename(response, url);
      await this.offlineStorage.savePdf(tkt, base64, filename);
      return true;
    } catch (e) {
      console.warn(`[CacheWarmup] savePdfToCache failed for tkt ${tkt}:`, e);
      return false;
    }
  }

  /**
   * Validates that a fetched blob is really a PDF. A 200-OK HTML login page looks fine
   * by HTTP status — the magic-byte check is what prevents poisoned cache entries.
   */
  private async isValidPdfBlob(blob: Blob, response: Response): Promise<boolean> {
    if (blob.size < 5) return false;

    const contentType = (response.headers.get('content-type') || '').toLowerCase();
    // Reject explicit HTML regardless of size
    if (contentType.includes('text/html')) return false;

    // Read first 5 bytes and check for "%PDF-"
    try {
      const head = new Uint8Array(await blob.slice(0, 5).arrayBuffer());
      return head[0] === 0x25 && head[1] === 0x50 && head[2] === 0x44 && head[3] === 0x46 && head[4] === 0x2d;
    } catch {
      return false;
    }
  }

  private async cachePdf(order: Order): Promise<void> {
    await this.savePdfToCache(order.tkt.toString(), order.routePdfGuide);
  }

  /**
   * Stale-while-revalidate fetch. Compares the freshly-fetched PDF against the currently-
   * cached one by SHA-256. If they differ (or nothing was cached), the cache is updated
   * and the fresh bytes + filename are returned so the viewer can swap live. Returns null
   * when nothing changed, the network failed, or the server returned a non-PDF.
   *
   * This is the mechanism that rescues drivers from the "guía siendo procesada" placeholder:
   * once the backend has the real PDF ready, the next open silently triggers this check,
   * the cache is corrected, and the viewer re-renders with the real guía without the user
   * having to close and reopen it.
   */
  async fetchAndCacheIfChanged(
    tkt: string,
    url: string,
  ): Promise<{ bytes: Uint8Array; filename: string } | null> {
    if (!url) return null;
    try {
      const response = await fetch(url);
      if (!response.ok) return null;
      const blob = await response.blob();
      if (!(await this.isValidPdfBlob(blob, response))) return null;

      const freshBytes = new Uint8Array(await blob.arrayBuffer());
      const freshHash  = await this.hashBytes(freshBytes);

      const cached = await this.offlineStorage.getPdf(tkt).catch(() => null);
      if (cached) {
        try {
          const b64 = cached.base64.includes(',') ? cached.base64.split(',')[1] : cached.base64;
          const raw = atob(b64);
          const cachedBytes = new Uint8Array(raw.length);
          for (let i = 0; i < raw.length; i++) cachedBytes[i] = raw.charCodeAt(i);
          const cachedHash = await this.hashBytes(cachedBytes);
          if (cachedHash === freshHash) return null; // unchanged — nothing to do
        } catch { /* decode failed — treat as changed */ }
      }

      const base64   = await this.blobToBase64(blob);
      const filename = this.extractFilename(response, url);
      await this.offlineStorage.savePdf(tkt, base64, filename);
      return { bytes: freshBytes, filename };
    } catch (e) {
      console.warn(`[CacheWarmup] fetchAndCacheIfChanged failed for tkt ${tkt}:`, e);
      return null;
    }
  }

  private async hashBytes(bytes: Uint8Array): Promise<string> {
    const buf = await crypto.subtle.digest('SHA-256', new Uint8Array(bytes));
    return Array.from(new Uint8Array(buf))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  private extractFilename(response: Response, url: string): string {
    // 1. Try Content-Disposition header: filename="guia_1234.pdf" or filename*=UTF-8''guia_1234.pdf
    const cd = response.headers.get('content-disposition');
    if (cd) {
      const match = cd.match(/filename\*?=['"]?(?:UTF-\d['"]*)?([^;\r\n"']+)['"]?/i);
      if (match?.[1]) return match[1].trim();
    }
    // 2. Fall back to the last path segment of the URL (before query string)
    try {
      const pathname = new URL(url).pathname;
      const segment = pathname.split('/').pop();
      if (segment && segment.toLowerCase().endsWith('.pdf')) return segment;
    } catch { /* ignore malformed URLs */ }
    return 'guia.pdf';
  }

  // ─── Reference data ───────────────────────────────────────────────────────

  private async cacheReferenceData(): Promise<void> {
    if (!this.orderService.hasAuthKey()) return;
    // These methods are cache-first and only hit the API when the local cache is empty.
    const [returnData, rejectData] = await Promise.all([
      this.orderService.fetchReturnReasonsDirect().pipe(take(1)).toPromise().catch(() => null),
      this.orderService.fetchRejectReasonsDirect().pipe(take(1)).toPromise().catch(() => null),
    ]);

    if (returnData) await this.offlineStorage.saveReturnReasons(returnData as any);
    if (rejectData) await this.offlineStorage.saveRejectReasons(rejectData as any);
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  /** Operador de planta: solo control de combustible, sin módulo de viajes. */
  private isSoloControlCombustible(): boolean {
    return this.executionContext.getAuthenticated()?.role === 'operador-planta';
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Processes an array in chunks of `concurrency` size sequentially.
   * Chunk items run in parallel; chunks themselves run one after another.
   */
  private async processWithConcurrency<T>(
    items: T[],
    concurrency: number,
    fn: (item: T) => Promise<void>
  ): Promise<void> {
    for (let i = 0; i < items.length; i += concurrency) {
      const chunk = items.slice(i, i + concurrency);
      await Promise.all(chunk.map(fn));
    }
  }
}
